import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import Modal, { ModalProps } from 'react-native-modal';
import Svg from './Svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../constants/Colors';
import Layout from '../constants/Layout';
import { useLocalization } from '../store/utils/useLocalization';

/**
 * Properties for a custom button in the bottom row of the modal.
 */
interface StyledModalButtonProps {
  /**
   * Callback to execute if the user clicks the button.
   */
  onPress: () => void;
  /**
   * Key in the translations locale file for the string to show on the button.
   * Does not support placeholders, but must be a translated string, not plaintext.
   */
  localeKey: string;
  /**
   * Test ID to put on the button.
   * Use sparingly and prefer to test by the user-readable text.
   */
  testID?: string;
  /**
   * True if the button should show as filled in.
   * Typically used to give a visual indication of which button is the default action.
   */
  filled?: boolean;
  /**
   * True if the button should display but be disabled.
   */
  disabled?: boolean;
}

/**
 * Properties to configure the modal.
 * Extends {@link ModalProps} so any of those properties
 * can also be provided.
 */
export interface StyledModalProps extends Partial<ModalProps> {
  /**
   * Callback to handle when the user clicks the close icon
   * (by default the X icon in the upper right).
   * If no callback is provided, then the icon will not be visible.
   * In this case the parent should provide different mechanisms
   * for closing (such as some other operations completing).
   */
  onClose?: () => void;
  /**
   * Text for the header bar of the modal.
   * If not provided then the title bar will still be visible but blank.
   */
  headerText?: string;
  /**
   * Custom buttons to show in the bottom row of the modal.
   * Buttons will show in the order passed, from left to right.
   * If not present then the bottom row won't show and the modal will only
   * contain the child components.
   */
  buttons?: StyledModalButtonProps[];
}

/**
 * Modal that uses a consistent style across other modals in the app.
 *
 * @param props Properties to use for the modal.
 *      Passthrough to underlying modal allowed.
 */
export function StyledModal(props: StyledModalProps) {
  const { t } = useLocalization();
  const safeArea = useSafeAreaInsets();

  return (
    <Modal
      {...(props as ModalProps)}
      style={[
        props.style,
        styles.modal,
        {
          // augment stylesheet with safe area inserts
          top: safeArea.top,
          left: safeArea.left,
          right: safeArea.right,
          bottom: safeArea.bottom,
        },
      ]}
      onBackdropPress={props.onClose}
    >
      <View style={styles.modalContent}>
        <View style={styles.header}>
          <Text style={styles.headerText}>{props.headerText}</Text>
          {props.onClose && (
            <TouchableOpacity
              testID={props.testID ? `${props.testID}-closeIcon` : undefined}
              onPress={props.onClose}
            >
              <Svg
                icon="close"
                color={styles.headerClose.color}
                width={styles.headerClose.width}
                height={styles.headerClose.height}
              />
            </TouchableOpacity>
          )}
        </View>
        <ScrollView style={styles.mainContent} automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.childContent}>{props.children}</View>
          {props.buttons && props.buttons.length > 0 && (
            <View style={styles.buttonRow} testID={props.testID ? `${props.testID}-buttonRow` : undefined}>
              {props.buttons.map((button, key) => (
                <TouchableOpacity
                  key={key}
                  onPress={button.onPress}
                  style={[
                    styles.formButton,
                    ...(button.filled ? [styles.filledButton] : []),
                    ...(button.disabled ? [styles.disabledButton] : []),
                  ]}
                  testID={button.testID}
                  disabled={button.disabled}
                >
                  <Text
                    style={[
                      styles.buttonText,
                      ...(button.filled ? [styles.filledButtonText] : []),
                      ...(button.disabled ? [styles.disabledButtonText] : []),
                    ]}
                  >
                    {t(button.localeKey)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    justifyContent: 'center',
    backgroundColor: Colors.white,
    width: Layout.isSmall ? 360 : 520,
  },
  header: {
    height: 66,
    width: '100%',
    backgroundColor: Colors.primaryColor,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  headerText: {
    color: Colors.white,
    fontSize: 20,
    fontWeight: 500,
  },
  headerClose: {
    color: Colors.white,
    width: 24,
    height: 24,
  },
  mainContent: {
    backgroundColor: Colors.lightBackground,
    width: '100%',
    maxHeight: 0.6 * Layout.fullHeight,
    height: 'auto',
  },
  childContent: {
    padding: 12,
  },
  buttonRow: {
    height: 72,
    width: '100%',
    backgroundColor: Colors.lightGrayBackground,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 12,
  },
  formButton: {
    borderRadius: 4,
    marginHorizontal: 13,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: Layout.isCompactScreen ? '100%' : '50%',
    borderColor: Colors.primaryColor,
    borderWidth: 1,
  },
  buttonText: {
    color: Colors.primaryColor,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  filledButton: {
    borderWidth: 0,
    backgroundColor: Colors.primaryColor,
  },
  filledButtonText: {
    color: Colors.white,
  },
  disabledButton: {
    borderWidth: 0,
    backgroundColor: Colors.grayButton,
  },
  disabledButtonText: {
    color: Colors.white,
  },
});
